import { AlertTriangle, Cable, Clock, ShoppingBag } from 'lucide-react';

import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useGbpHealthBanner, usePosHealthBanner, useStaleIngestBanner, useAllStockRejectedBanner } from '@/hooks/useHealthBanners';

import { HealthBanner } from './HealthBanner';

/**
 * Translates each signal hook's output into the matching `HealthBanner`
 * render. Lives here (not inside `HealthBanner`) so the primitive stays
 * pure presentation and the AppLayout doesn't carry per-banner copy.
 *
 * Render order (top→bottom) per the product call on this ticket:
 *   1. POS-disconnected   (root cause — everything downstream depends on it)
 *   2. GBP-revoked        (Google channel-specific; orthogonal to POS)
 *   3. All-stock-rejected (feed-health failure)
 *   4. Stale-ingest       (suppressed when POS-disconnected fires; see hook)
 *
 * Each banner is independently gated; multiple stack when conditions
 * overlap, mirroring the existing ExpiredTopBanner + LifecycleBanner
 * precedent.
 */
const STALE_TEXT = {
	soft: { title: 'Your stock hasn’t synced in over a week', severity: 'soft' },
	warning: { title: 'Your stock is two weeks out of date', severity: 'warning' },
	error: { title: 'Your stock has been frozen for over a month', severity: 'error' }
};

export function HealthBannerStack() {
	const { shop } = useShop();
	const { shopPath } = useShopPath();

	const pos = usePosHealthBanner();
	const gbp = useGbpHealthBanner();
	const rejected = useAllStockRejectedBanner();
	const stale = useStaleIngestBanner();

	const shopId = shop?.id ?? null;

	return (
		<>
			{pos ? (
				<HealthBanner
					id="pos-disconnected"
					severity="urgent"
					icon={Cable}
					title="Your POS isn’t connected"
					body="Stock changes from your till aren’t reaching any of your channels."
					ctaLabel="Reconnect POS"
					ctaTo={shopPath('/onboarding?step=3')}
					shopId={shopId}
				/>
			) : null}

			{gbp ? (
				<HealthBanner
					id="gbp-revoked"
					severity="urgent"
					icon={AlertTriangle}
					title="Your Google Business Profile is disconnected"
					body="Reconnect to keep Google Local Listings and Local Ads running."
					ctaLabel="Reconnect Google"
					ctaTo={shopPath('/onboarding?step=2')}
					shopId={shopId}
				/>
			) : null}

			{rejected ? (
				<HealthBanner
					id="all-stock-rejected"
					severity="urgent"
					icon={ShoppingBag}
					title="None of your products are visible to shoppers"
					body="Every item in your catalogue is being rejected — fix the product errors to start showing again."
					ctaLabel="Fix product errors"
					ctaTo={shopPath('/inventory')}
					shopId={shopId}
				/>
			) : null}

			{stale ? (
				// At warning (14d) / error (30d) the feed is frozen, not late — the
				// useful action is the reconnect flow, so the single CTA swaps to it
				// (NEA-5887). The 7d soft banner stays a heads-up pointing at the
				// accuracy stats.
				<HealthBanner
					id={`stale-ingest-${stale.severity}`}
					severity={STALE_TEXT[stale.severity].severity}
					icon={Clock}
					title={STALE_TEXT[stale.severity].title}
					body={`Last sync ${stale.days} days ago. Every channel is showing what your shop had then.`}
					ctaLabel={stale.severity === 'soft' ? 'Check connection' : 'Reconnect POS'}
					ctaTo={stale.severity === 'soft' ? shopPath('/inventory/connection') : shopPath('/onboarding?step=3')}
					shopId={shopId}
				/>
			) : null}
		</>
	);
}
